"use client"
import { useMemo } from "react"
import type { PlanId } from "@/lib/branchTypes"
import { useHudContext } from "../../hud/shared/playbookHUDInfra"
import { defaultGraphData } from "../../extrapolated-graph/graphConstants"
import type { ZoomKeyframesByTrack } from "../../extrapolated-graph/zoomKeyframes"
import {
  HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX,
  HUD87_CURRENT_POINT_DOT_RADIUS_PX,
} from "../../hud/variants"
import type { CapabilityPoint } from "../MobileCapabilityLineChart"
import type { MobileStripBandSettings } from "../mobileStripBandSettings"
import { sampleSeriesAt, toTimeValuePoints } from "./interpolate"
import { stripPlotArea } from "./stripLayout"
import { COMPACT_STRIP_VARIANTS, type MobileStripDesignId } from "./stripVariants"
import { useStripCamera } from "./camera/useStripCamera"
import { buildStripYForValue } from "./camera/stripYScale"
import { SAVED_BAND_SETTINGS, createRiskBandModel } from "./risk-band/riskBandModel"
import { buildBandSvgArt } from "./risk-band/bandSvgLayers"
import { RiskBandSolidFill } from "./risk-band/RiskBandSolidFill"
import { DitherFieldCanvas } from "./risk-band/DitherFieldCanvas"
import { makeLineProjection } from "./capability-line/linePaths"
import {
  MOBILE_STRIP_LINE_STROKE_PX,
  MOBILE_STRIP_TIP_DOT_RADIUS_PX,
  buildCapabilityLineArt,
} from "./capability-line/capabilityLineArt"
import { CapabilityLineSvg } from "./capability-line/CapabilityLineSvg"
import {
  CHOICE_POINT_DITHER_OPACITY_SCALE,
  PLAN_PREVIEW_FADE_MS,
  usePlanPreviewFan,
} from "./plan-fan/planPreviewFan"
import { PlanPreviewFanSvg } from "./plan-fan/PlanPreviewFanSvg"
import { MobileStripGlobeLayer } from "./MobileStripGlobeLayer"
import { StripDateLabel } from "./StripDateLabel"

// The super-compact mobile HUD strip: globe, capability line,
// takeover-risk band, and date inside one 76px-tall bottom strip. This
// component wires the concepts together; each lives in its own module:
//   camera/          which window + y-max is on screen (the frame)
//   risk-band/       the red distribution behind the line
//   capability-line/ the active plan's line + tip dot
//   plan-fan/        the five-plan preview at the choice point

type MobileCompactHudStripProps = {
  variant: MobileStripDesignId
  width: number
  height: number
  // Smoothed story date; drives the line reveal and the x-domain.
  currentDate: Date
  // Full capability line (shared history + the active plan's future).
  points: CapabilityPoint[]
  textColor: string
  backgroundColor: string
  globePaused: boolean
  // While a plan hover/tap autoplay sweeps the date, the frame stays
  // frozen — the reveal belongs to the real reading beat.
  dateIsHoverAutoplay?: boolean
  // /zoom-editor live-preview sources, so edits render immediately
  // instead of the saved JSON. Leave unset everywhere else.
  zoomKeyframesOverride?: ZoomKeyframesByTrack
  bandSettingsOverride?: MobileStripBandSettings
}

export const MobileCompactHudStrip = ({
  variant,
  width,
  height,
  currentDate,
  points,
  textColor,
  backgroundColor,
  globePaused,
  dateIsHoverAutoplay = false,
  zoomKeyframesOverride,
  bandSettingsOverride,
}: MobileCompactHudStripProps) => {
  const config = COMPACT_STRIP_VARIANTS[variant]
  const band = bandSettingsOverride ?? SAVED_BAND_SETTINGS
  const ctx = useHudContext()
  const plot = stripPlotArea(width, height)
  const isDesktopMirror = config.graphSource === "desktop"

  // The active plan's line as a sampled time series.
  const linePoints = useMemo(() => toTimeValuePoints(points), [points])
  const firstT = linePoints.length > 0 ? linePoints[0].time : 0
  const currentT = Math.max(currentDate.getTime(), firstT + 1)
  const lineValueAt = (time: number) => sampleSeriesAt(linePoints, time)

  const camera = useStripCamera({
    isDesktopMirror,
    currentDate,
    currentT,
    firstT,
    activePlan: ctx.activePlan,
    isChoicePointOnScreen: ctx.isChoicePointOnScreen === true,
    dateIsHoverAutoplay,
    zoomKeyframesOverride,
    plot,
  })

  const bounds = defaultGraphData.lossOfControlByPlan[ctx.activePlan]
    ?? defaultGraphData.lossOfControlByPlan.A
  const boundsMinSeries = useMemo(() => toTimeValuePoints(bounds.min), [bounds])
  const boundsMaxSeries = useMemo(() => toTimeValuePoints(bounds.max), [bounds])
  const riskBand = createRiskBandModel({
    isDesktopMirror,
    band,
    boundsMinSeries,
    boundsMaxSeries,
    lineValueAt,
    camera,
  })
  const yForValue = buildStripYForValue({
    isDesktopMirror,
    config,
    plot,
    camera,
    currentT,
    linePoints,
    lineValueAt,
    boundsMaxSeries,
    bandTopValueAt: riskBand.topValueAt,
  })
  const proj = makeLineProjection(plot, camera.xForTime, yForValue)

  const bandArt = useMemo(
    () => buildBandSvgArt({ plot, band, model: riskBand, yForValue, timeForX: camera.timeForX }),
    // Same effective deps as the band memo had before this refactor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bounds, camera.viewStartT, camera.viewSpan, plot.innerWidth, currentT, camera.frameYMax, variant, band],
  )

  const lineArt = buildCapabilityLineArt({
    isDesktopMirror,
    plot,
    camera,
    proj,
    linePoints,
    lineValueAt,
    currentT,
  })

  // The fan stays up the WHOLE time the choose-your-path block is on
  // screen — before AND after a pick; picking just makes that plan the
  // solid emphasized line. Desktop-mirror only: the squish variants have
  // no choice-point frame to draw it into.
  const arePlanPreviewLinesShown = isDesktopMirror && ctx.isChoicePointOnScreen === true
  const emphasizedPlan: PlanId = ctx.hoveredPlan ?? ctx.selectedPlan ?? "D"
  // Before any hover/pick the fan stays anonymous — the default-
  // emphasized D gets no letter until it's actually pointed at.
  const letterLabeledPlan: PlanId | null = ctx.hoveredPlan ?? ctx.selectedPlan ?? null
  const fan = usePlanPreviewFan({
    isShown: arePlanPreviewLinesShown,
    emphasizedPlan,
    proj,
    bleedStartT: lineArt.bleedStartT,
    bleedEndT: lineArt.bleedEndT,
    viewEndT: camera.viewEndT,
    plot,
    avoidYearPill: config.datePillPlacement === "bottom-right",
  })

  // Near-full dither while the fan is up — the dot-size gradient does
  // the legibility work; eases back to full the moment the fan leaves.
  const bandDisplayOpacity = arePlanPreviewLinesShown
    ? band.bandOpacity * CHOICE_POINT_DITHER_OPACITY_SCALE
    : band.bandOpacity

  return (
    <div className="absolute inset-0" style={{ color: textColor }}>
      {/* globeOverflowsCard variants lose the card container's clipping
          (so the escaped globe can overlap the page above the strip) —
          the graph + pill clip to the rounded card HERE instead. */}
      <div
        className={config.globeOverflowsCard
          ? "absolute inset-0 overflow-hidden"
          : "absolute inset-0"}
        style={config.globeOverflowsCard
          ? { borderRadius: config.radiusPx }
          : undefined}
      >
        {/* Per-dot canvas dither, under the SVG so the line, edge
            stroke, and pill paint above it. */}
        {band.fillStyle === "dither" && (
          <DitherFieldCanvas
            opacity={bandDisplayOpacity}
            fadeMs={PLAN_PREVIEW_FADE_MS}
            inputs={{
              plot,
              band,
              model: riskBand,
              yForValue,
              timeForX: camera.timeForX,
              exclusionLine: lineArt.exclusionLinePoints,
              fanPolylines: fan.exclusionPolylines,
              choiceBlend: camera.choiceBlend,
              tip: {
                x: lineArt.tip.x,
                y: lineArt.tip.y,
                radius: isDesktopMirror
                  ? HUD87_CURRENT_POINT_DOT_RADIUS_PX
                  : MOBILE_STRIP_TIP_DOT_RADIUS_PX,
              },
              lineHalfStrokePx: (isDesktopMirror
                ? HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX
                : MOBILE_STRIP_LINE_STROKE_PX) / 2,
            }}
          />
        )}
        <svg
          width={width}
          height={height}
          className="absolute left-0 top-0 block overflow-hidden"
        >
          <RiskBandSolidFill
            art={bandArt}
            band={band}
            opacity={bandDisplayOpacity}
            fadeMs={PLAN_PREVIEW_FADE_MS}
          />
          {fan.isMounted && (
            <PlanPreviewFanSvg
              fan={fan}
              isShown={arePlanPreviewLinesShown}
              letterLabeledPlan={letterLabeledPlan}
              backgroundColor={backgroundColor}
            />
          )}
          <CapabilityLineSvg
            art={lineArt}
            isDesktopMirror={isDesktopMirror}
            textColor={textColor}
            backgroundColor={backgroundColor}
          />
        </svg>
        {/* Bottom-sheet grabber over the dither: the "pull this open for
            more details" affordance, knocked out in the page color. */}
        {config.expandHandle !== null && (
          <div
            className="pointer-events-none absolute left-1/2 top-1.5 -translate-x-1/2 rounded-full"
            style={{
              width: config.expandHandle.widthPx,
              height: config.expandHandle.heightPx,
              backgroundColor,
            }}
          />
        )}
        <StripDateLabel
          config={config}
          year={currentDate.getFullYear()}
          textColor={textColor}
          backgroundColor={backgroundColor}
        />
      </div>
      {/* Escaped globes are mounted by MobileHUD outside the card (see
          globeOverflowsCard); globeHidden variants have no earth at all. */}
      {!config.globeOverflowsCard && !config.globeHidden && (
        <MobileStripGlobeLayer
          variant={variant}
          height={height}
          globePaused={globePaused}
        />
      )}
    </div>
  )
}
